"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState, type ComponentProps } from "react";
import { ATLAS_MIN_WIDTH } from "@/components/maps/MapEntryAtlas";
import type { MapPin } from "@/design-data/map-atlas-data";
import type { MapAtlas as MapAtlasComponent } from "@/components/MapAtlas";

// `ssr: false` is only legal inside a Client Component boundary - HomePage
// itself is an async Server Component, so the dynamic import has to live
// here rather than inline there. Leaflet is DOM/window-only, and this map
// is a below-the-fold, interactive-only embed, so there's nothing useful to
// render server-side for it.
//
// The `loading` placeholder is not decorative: without it this slot is 0px
// tall until the Leaflet chunk finishes loading, then jumps to full height.
// Because the map sits above most of the homepage, that late growth made the
// browser's scroll anchoring push the scroll position down by ~1000px while
// the visitor was already reading further down the page. The placeholder
// reserves the exact final height (see .pbd-map-placeholder in globals.css,
// which shares the map's own 980px breakpoint) so the swap costs no layout
// shift at all.
function MapPlaceholder() {
  return (
    <div
      className="pbd-map-placeholder overflow-hidden rounded-[10px] border border-[var(--border-subtle)] bg-[var(--surface)]"
      role="status"
      aria-label="Loading the map"
    />
  );
}

const MapAtlasClient = dynamic(() => import("@/components/MapAtlas").then((mod) => mod.MapAtlas), {
  ssr: false,
  loading: () => <MapPlaceholder />,
});

type MapAtlasProps = ComponentProps<typeof MapAtlasComponent>;

/**
 * The homepage map, with its database pins fetched rather than passed in.
 *
 * Passing them as a prop from the server component serialised ~720 pins into
 * the homepage HTML — about half the page — which the browser had to get
 * through before the hero finished loading. They now come from /api/map-pins,
 * requested as soon as this mounts, in parallel with the Leaflet chunk.
 *
 * The map is not rendered until the pins are in. MapAtlas matches weather
 * readings to pins by position in its pin list, so adding pins after its
 * weather request would attach readings to the wrong places. If the request
 * fails the map still renders, with the static catalogue's pins.
 *
 * A caller that already has the pins can still pass `extraSightPins`.
 */
export function MapAtlas(props: MapAtlasProps) {
  const given = props.extraSightPins;
  const [fetched, setFetched] = useState<MapPin[] | null>(null);
  // Phones get the same live atlas as computers (owner, 1 Oct 2026: "mobile
  // map navigation should also behave like the computer"), but only once the
  // visitor scrolls near it: plotting ~800 markers costs a phone most of a
  // second, which must not land on the top of the homepage. Computers load it
  // straight away, as before. The placeholder holds the map's exact height
  // meanwhile, so nothing below it moves.
  const slotRef = useRef<HTMLDivElement>(null);
  const [wanted, setWanted] = useState(false);

  useEffect(() => {
    if (given) return;
    if (window.matchMedia(`(min-width: ${ATLAS_MIN_WIDTH}px)`).matches) {
      const timer = window.setTimeout(() => setWanted(true), 0);
      return () => window.clearTimeout(timer);
    }
    const slot = slotRef.current;
    if (!slot) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setWanted(true);
          observer.disconnect();
        }
      },
      // On phones the map is the section right after the hero, so "near" would
      // be "at once": wait until it is actually on screen.
      { rootMargin: "0px" },
    );
    observer.observe(slot);
    return () => observer.disconnect();
  }, [given]);

  useEffect(() => {
    if (given || !wanted) return;
    let cancelled = false;
    fetch("/api/map-pins")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { pins?: Partial<MapPin>[] } | null) => {
        if (cancelled) return;
        // The route drops the placeholder temperature to save bytes.
        setFetched((data?.pins ?? []).map((pin) => ({ temp: "—", ...pin }) as MapPin));
      })
      .catch(() => {
        if (!cancelled) setFetched([]);
      });
    return () => {
      cancelled = true;
    };
  }, [given, wanted]);

  const pins = given ?? fetched;
  if (!pins) {
    return (
      <div ref={slotRef}>
        <MapPlaceholder />
      </div>
    );
  }
  return <MapAtlasClient {...props} extraSightPins={pins} />;
}
